import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import getRelativeTime from "../../getRelativeTime";

function Notification({
  icon = "file-earmark-text",
  title,
  description,
  created,
  isRead,
  route,
  client,
  supplier,
  onClick,
  onMenuAction,
  notification,
  selectable,
  selected,
  onSelectChange,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    function handleClickOutside(e) {
      const insideMenu = menuRef.current?.contains(e.target);
      const insideDropdown = dropdownRef.current?.contains(e.target);
      if (!insideMenu && !insideDropdown) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  useLayoutEffect(() => {
    if (menuOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownStyle({
        position: "fixed",
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
        left: "auto",
        minWidth: "160px",
        zIndex: 1060,
      });
    }
  }, [menuOpen]);

  const handleRowClick = (e) => {
    if (menuRef.current && menuRef.current.contains(e.target)) return;
    if (dropdownRef.current?.contains(e.target)) return;
    if (e.target.closest?.("input[type='checkbox']")) return;
    onClick?.();
  };

  const handleCheckboxChange = (e) => {
    e.stopPropagation();
    onSelectChange?.(!selected);
  };

  const handleMenuClick = (e) => {
    e.stopPropagation();
    setMenuOpen((prev) => !prev);
  };

  const handleOption = (action) => {
    setMenuOpen(false);
    onMenuAction?.(notification, action);
  };

  return (
    <li
      className={`list-group-item list-group-item-action d-flex align-items-start gap-3 py-3 ${!isRead ? "" : "bg-light"}`}
      style={{ cursor: onClick ? "pointer" : "default" }}
      onClick={handleRowClick}
    >
      {selectable && (
        <div
          className="d-flex align-items-center flex-shrink-0 pt-1"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            className="form-check-input"
            checked={!!selected}
            onChange={handleCheckboxChange}
            aria-label="Select notification"
          />
        </div>
      )}

      <div
        className="d-flex align-items-center justify-content-center rounded-circle bg-primary bg-opacity-10 text-primary flex-shrink-0"
        style={{ width: "38px", height: "38px" }}
      >
        <i className={`bi bi-${icon}`}></i>
      </div>

      <div className="flex-grow-1 overflow-hidden min-w-0">
        <div className="d-flex justify-content-between align-items-start gap-2">
          <p className="mb-0 fw-semibold text-truncate small">{title}</p>
          {!isRead && (
            <span
              className="badge bg-danger rounded-pill flex-shrink-0"
              style={{ fontSize: "6px" }}
            >
              ●
            </span>
          )}
        </div>

        <div className="d-flex gap-1">
          {client && (
            <p
              className="mb-0 small border border-success rounded-pill px-2 bg-opacity-10 bg-success text-muted fw-bold"
              style={{ fontSize: "11px" }}
            >
              <i className="bi bi-person me-1" />
              {client.name}
            </p>
          )}
          {supplier && (
            <p className="mb-0 text-muted small" style={{ fontSize: "11px" }}>
              <i className="bi bi-person me-1" />
              {supplier.name}
            </p>
          )}
        </div>

        <p
          className="mb-0 text-muted text-truncate"
          style={{ fontSize: "12px" }}
        >
          {description}
        </p>

        <p className="mb-0 text-muted" style={{ fontSize: "11px" }}>
          {getRelativeTime(created)}
        </p>
      </div>

      {onMenuAction && (
        <div className="position-relative flex-shrink-0" ref={menuRef}>
          <button
            ref={buttonRef}
            type="button"
            className="btn btn-sm btn-link text-muted p-1 rounded"
            onClick={handleMenuClick}
            aria-label="Options"
            style={{ lineHeight: 1 }}
          >
            <i className="bi bi-three-dots-vertical fs-5" />
          </button>

          {menuOpen &&
            createPortal(
              <div
                ref={dropdownRef}
                className="dropdown-menu show shadow-sm"
                style={dropdownStyle}
              >
                {!isRead && (
                  <button
                    type="button"
                    className="dropdown-item small d-flex align-items-center gap-2"
                    onClick={() => handleOption("mark-read")}
                  >
                    <i className="bi bi-check2" />
                    Mark as read
                  </button>
                )}
                {isRead && (
                  <button
                    type="button"
                    className="dropdown-item small d-flex align-items-center gap-2"
                    onClick={() => handleOption("mark-unread")}
                  >
                    <i className="bi bi-envelope" />
                    Mark as unread
                  </button>
                )}
                {route && (
                  <button
                    type="button"
                    className="dropdown-item small d-flex align-items-center gap-2"
                    onClick={() => handleOption("open")}
                  >
                    <i className="bi bi-box-arrow-up-right" />
                    Open
                  </button>
                )}
                <button
                  type="button"
                  className="dropdown-item small d-flex align-items-center gap-2 text-danger"
                  onClick={() => handleOption("delete")}
                >
                  <i className="bi bi-trash" />
                  Delete
                </button>
              </div>,
              document.body,
            )}
        </div>
      )}
    </li>
  );
}

export default Notification;
